/**
 * Locațiile (punctele de lucru) — fiecare cu propriile QR-uri și ecrane.
 * Include programul de funcționare, pentru că starea „deschis/închis" apare pe meniul public.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Clock24,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  cx,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';
import { useToast } from '../components/ui.js';

interface Location {
  id: string;
  public_id: string;
  slug: string;
  name: string;
  city: string | null;
  address_line1: string | null;
  phone: string | null;
  is_active: boolean;
  timezone: string;
  restaurant_id: string;
  restaurant_name: string;
  qr_count: number;
  display_count: number;
}

interface Restaurant {
  id: string;
  name: string;
}

const WEEKDAYS = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];

export function LocationsPage() {
  const session = useSession();
  const toast = useToast();
  const locations = useResource<Location[]>(() => api.get<Location[]>('/locations'), [session.activeAccountId]);
  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), [session.activeAccountId]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Location | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Location | null>(null);
  const [hoursTarget, setHoursTarget] = useState<Location | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState({
    restaurantId: '',
    name: '',
    city: '',
    address: '',
    phone: '',
    isActive: true,
  });

  const canCreate = session.can('location.create');
  const canUpdate = session.can('location.update');
  const canDelete = session.can('location.delete');

  async function create() {
    setBusy(true);
    setErrors({});
    setFormError(null);
    const restaurantId = form.restaurantId || restaurants.data?.[0]?.id;
    if (!restaurantId) {
      setFormError('Creează mai întâi un restaurant.');
      setBusy(false);
      return;
    }
    try {
      await api.post(`/restaurants/${restaurantId}/locations`, {
        name: form.name,
        ...(form.city.trim() ? { city: form.city.trim() } : {}),
        ...(form.address.trim() ? { addressLine1: form.address.trim() } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        isActive: form.isActive,
      });
      toast.success('Locație adăugată', 'Poți genera acum codurile QR pentru mese.');
      setCreateOpen(false);
      setForm({ restaurantId: '', name: '', city: '', address: '', phone: '', isActive: true });
      await locations.reload();
    } catch (error) {
      setErrors(fieldErrors(error));
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    if (!editTarget) return;
    setBusy(true);
    setFormError(null);
    try {
      await api.patch(`/locations/${editTarget.id}`, {
        name: editTarget.name,
        city: editTarget.city?.trim() ?? '',
        addressLine1: editTarget.address_line1?.trim() ?? '',
        phone: editTarget.phone?.trim() ?? '',
      });
      toast.success('Locația a fost actualizată');
      setEditTarget(null);
      await locations.reload();
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.del(`/locations/${deleteTarget.id}`);
      toast.success('Locația a fost ștearsă', 'Codurile QR rămân, fără locație asociată.');
      setDeleteTarget(null);
      await locations.reload();
    } catch (error) {
      toast.error('Nu am putut șterge locația', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(location: Location) {
    try {
      await api.patch(`/locations/${location.id}`, { isActive: !location.is_active });
      toast.success(location.is_active ? 'Locație dezactivată' : 'Locație activată');
      await locations.reload();
    } catch (error) {
      toast.error('Nu am putut modifica starea', errorMessage(error));
    }
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Locații"
        subtitle="Fiecare locație are meniul ei publicat, QR-urile ei și ecranele ei."
        actions={
          canCreate ? (
            <Button icon={<Icon name="plus" size={17} />} onClick={() => setCreateOpen(true)}>
              Locație nouă
            </Button>
          ) : null
        }
      />

      {locations.loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="flex flex-col gap-3">
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-16 w-full" />
            </Card>
          ))}
        </div>
      ) : locations.error ? (
        <InlineAlert tone="warning" title="Nu am putut încărca locațiile">
          {locations.error}
        </InlineAlert>
      ) : !locations.data?.length ? (
        <EmptyState
          icon={<Icon name="pin" size={28} />}
          title="Nicio locație încă"
          description="Adaugă prima locație pentru a genera coduri QR pentru mese."
          action={
            canCreate ? (
              <Button icon={<Icon name="plus" size={17} />} onClick={() => setCreateOpen(true)}>
                Adaugă locație
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {locations.data.map((location) => (
            <Card as="li" key={location.id} className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <h3 className="truncate font-display text-title text-ink-950">{location.name}</h3>
                  <p className="truncate text-caption text-ink-400">
                    {location.restaurant_name}
                    {location.city ? ` · ${location.city}` : ''}
                  </p>
                </div>
                {location.is_active ? <Badge tone="success">activă</Badge> : <Badge tone="neutral">inactivă</Badge>}
              </div>

              <dl className="grid grid-cols-2 gap-3">
                <Stat label="Coduri QR" value={location.qr_count} icon="qr" />
                <Stat label="Ecrane" value={location.display_count} icon="display" />
              </dl>

              <p className="text-caption text-ink-400">
                {location.address_line1 ?? 'Adresa poate fi completată oricând'}
              </p>

              <div className="mt-auto flex flex-wrap gap-2">
                {canUpdate ? (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => setHoursTarget(location)}>
                      Program
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditTarget(location)}>
                      Editează
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => void toggleActive(location)}>
                      {location.is_active ? 'Dezactivează' : 'Activează'}
                    </Button>
                    {canDelete ? (
                      <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDeleteTarget(location)}>
                        Șterge
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </Card>
          ))}
        </ul>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Locație nouă"
        description="O locație = un punct de lucru cu propriile QR-uri și ecrane."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>
              Anulează
            </Button>
            <Button loading={busy} onClick={() => void create()}>
              Adaugă locația
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}
          <Field label="Restaurant" required errors={errors.restaurantId}>
            {(props) => (
              <Select
                {...props}
                value={form.restaurantId || restaurants.data?.[0]?.id || ''}
                onChange={(e) => setForm((current) => ({ ...current, restaurantId: e.target.value }))}
              >
                {(restaurants.data ?? []).map((restaurant) => (
                  <option key={restaurant.id} value={restaurant.id}>
                    {restaurant.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Numele locației" required errors={errors.name} hint="Apare pe meniul public, ca titlu.">
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                placeholder="Green Garden — Terasa"
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Oraș" errors={errors.city}>
              {(props) => (
                <Input
                  {...props}
                  value={form.city}
                  onChange={(e) => setForm((current) => ({ ...current, city: e.target.value }))}
                />
              )}
            </Field>
            <Field label="Telefon" errors={errors.phone}>
              {(props) => (
                <Input
                  {...props}
                  value={form.phone}
                  onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))}
                  placeholder="+40 721 123 456"
                />
              )}
            </Field>
          </div>
          <Field label="Adresă" errors={errors.addressLine1}>
            {(props) => (
              <Input
                {...props}
                value={form.address}
                onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
              />
            )}
          </Field>
          <Switch
            checked={form.isActive}
            onChange={(value) => setForm((current) => ({ ...current, isActive: value }))}
            label="Locația este activă"
            description="Locațiile inactive nu apar în meniul public."
          />
        </div>
      </Modal>

      {hoursTarget ? <HoursModal location={hoursTarget} onClose={() => setHoursTarget(null)} /> : null}

      <Modal
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        title="Editează locația"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditTarget(null)} disabled={busy}>
              Anulează
            </Button>
            <Button loading={busy} onClick={() => void saveEdit()}>
              Salvează
            </Button>
          </>
        }
      >
        {editTarget ? (
          <div className="flex flex-col gap-4">
            {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}
            <Field label="Nume" required>
              {(props) => (
                <Input
                  {...props}
                  value={editTarget.name}
                  onChange={(event) => setEditTarget({ ...editTarget, name: event.target.value })}
                />
              )}
            </Field>
            <Field label="Oraș">
              {(props) => (
                <Input
                  {...props}
                  value={editTarget.city ?? ''}
                  onChange={(event) => setEditTarget({ ...editTarget, city: event.target.value })}
                />
              )}
            </Field>
            <Field label="Adresă">
              {(props) => (
                <Input
                  {...props}
                  value={editTarget.address_line1 ?? ''}
                  onChange={(event) => setEditTarget({ ...editTarget, address_line1: event.target.value })}
                />
              )}
            </Field>
            <Field label="Telefon">
              {(props) => (
                <Input
                  {...props}
                  value={editTarget.phone ?? ''}
                  onChange={(event) => setEditTarget({ ...editTarget, phone: event.target.value })}
                />
              )}
            </Field>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void remove()}
        loading={busy}
        title={`Ștergi „${deleteTarget?.name ?? ''}"?`}
        description="Locația dispare din listă. Codurile QR create pentru ea rămân și pot fi mutate la altă locație."
        confirmLabel="Șterge locația"
      />
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon: 'qr' | 'display' }) {
  return (
    <div className="flex items-center gap-2.5 rounded-[var(--radius-sm)] bg-surface-3/60 px-3 py-2">
      <Icon name={icon} size={16} className="text-ink-400" />
      <div className="flex flex-col">
        <dt className="text-micro text-ink-400">{label}</dt>
        <dd className="nums text-body-s font-medium text-ink-950">{value}</dd>
      </div>
    </div>
  );
}

interface HourRow {
  weekday: number;
  opens_at: string | null;
  closes_at: string | null;
  is_closed: boolean;
}

function HoursModal({ location, onClose }: { location: Location; onClose: () => void }) {
  const toast = useToast();
  const resource = useResource<HourRow[]>(() => api.get<HourRow[]>(`/locations/${location.id}/hours`), [location.id]);
  const [rows, setRows] = useState<HourRow[] | null>(null);
  const [busy, setBusy] = useState(false);

  const current = rows ?? resource.data ?? [];
  const canUpdate = true;

  async function save() {
    setBusy(true);
    try {
      await api.put(`/locations/${location.id}/hours`, {
        hours: current.map((row) => ({
          weekday: row.weekday,
          isClosed: row.is_closed,
          opensAt: row.opens_at?.slice(0, 5) ?? null,
          closesAt: row.closes_at?.slice(0, 5) ?? null,
        })),
      });
      toast.success('Program salvat');
      onClose();
    } catch (error) {
      toast.error('Nu am putut salva programul', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Program — ${location.name}`}
      description={'Orele sunt ora României (București). Meniul public folosește acest program pentru starea deschis sau închis.'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Anulează
          </Button>
          <Button loading={busy} disabled={!canUpdate} onClick={() => void save()}>
            Salvează programul
          </Button>
        </>
      }
    >
      {resource.loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 7 }).map((_, index) => (
            <Skeleton key={index} className="h-10" />
          ))}
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-ink-100">
          {current.map((row) => (
            <li key={row.weekday} className="flex flex-wrap items-center gap-3 py-2.5 sm:flex-nowrap">
              <span className="w-24 text-body-s text-ink-950">{WEEKDAYS[row.weekday]}</span>
              <span className={cx('flex items-center gap-2', row.is_closed && 'opacity-40')}>
                <Clock24
                  value={row.opens_at?.slice(0, 5) ?? '10:00'}
                  disabled={row.is_closed}
                  onChange={(value) => {
                    const next = current.map((item) =>
                      item.weekday === row.weekday ? { ...item, opens_at: `${value}:00` } : item,
                    );
                    setRows(next);
                  }}
                />
                <span className="text-ink-400">–</span>
                <Clock24
                  value={row.closes_at?.slice(0, 5) ?? '22:00'}
                  disabled={row.is_closed}
                  onChange={(value) => {
                    const next = current.map((item) =>
                      item.weekday === row.weekday ? { ...item, closes_at: `${value}:00` } : item,
                    );
                    setRows(next);
                  }}
                />
              </span>
              <label className="ml-auto flex items-center gap-2 text-caption text-ink-600">
                <input
                  type="checkbox"
                  checked={row.is_closed}
                  onChange={(e) => {
                    const next = current.map((item) =>
                      item.weekday === row.weekday ? { ...item, is_closed: e.target.checked } : item,
                    );
                    setRows(next);
                  }}
                  className="h-4 w-4 rounded border-ink-200"
                />
                Închis
              </label>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
